/**
 * A Generic API adapter accepting a base url and headers. It will be implemented by any other api adapter.
 */
/* globals RequestInit */

import 'isomorphic-fetch';
import { v4 as uuidv4 } from '@lukeed/uuid';
import { stringify, parse } from 'query-string';
import { ErrorHandlerFunction, parseException } from './ErrorHandlers';
import { Config } from 'Common/appshell_types';

type GenericApiAdapterType = {
    apiBaseUrl: string;
    config: Config;
    userAgent: string;
    initialQueryParams?: object;
    headers?: object;
    userIP?: string;
};

export default class GenericApiAdapter {
    private readonly headers: object;

    private readonly apiBaseUrl: string;

    private readonly config: Config;

    public initialQueryParams: {};

    private readonly userAgent: string;

    private readonly userIP?: string;

    public constructor({
        apiBaseUrl,
        config,
        userAgent,
        initialQueryParams = {},
        headers = {},
        userIP,
    }: GenericApiAdapterType) {
        this.apiBaseUrl = apiBaseUrl;
        this.headers = headers;
        this.config = config;
        this.initialQueryParams = initialQueryParams;
        this.userAgent = userAgent;
        this.userIP = userIP;
    }

    private getHeaders(wwReqId: string, headers: object = {}): Headers {
        return new Headers({
            Accept: 'application/json',
            'x-app-version': `1.0.0-${this.config.buildVersion}`,
            'x-app-type': 'bella',
            'x-device-type': 'phone',
            'x-client-user-agent': this.userAgent,
            'ww-req-id': wwReqId,
            ...(this.userIP ? { 'x-forwarded-for': this.userIP } : {}),
            ...this.headers,
            ...headers,
        });
    }

    private getQueryString(queryParams: object): string {
        const mergedQueryParams = { ...this.initialQueryParams, ...queryParams };
        return `?${stringify(mergedQueryParams, { arrayFormat: 'comma' })}`;
    }

    // eslint-disable-next-line max-params
    public async get<T>(
        path: string,
        queryParams: object = {},
        headers: object = {},
        errorHandler: ErrorHandlerFunction,
        fullResponse = false,
        controller?: AbortController
    ): Promise<T> {
        const wwReqId = uuidv4();
        const queryString = this.getQueryString(queryParams);

        /**
         * This is circuit breaking API calls. In case we abort
         * any API calls we need to pass control (new AbortController)
         * and the API would be cancelled after serverAPITimeoutInMS.
         * For now, used only cart/count, in case the api takes long
         * on server, we cacnel it and then fetch again at client.
         */
        let abortTimeout;
        let signal;
        if (controller) {
            ({ signal } = controller);
            abortTimeout = setTimeout(() => {
                controller.abort();
            }, this.config.serverAPITimeoutInMS || 200);
        }

        try {
            const response = await fetch(this.apiBaseUrl + encodeURI(path) + queryString, {
                method: 'GET',
                headers: this.getHeaders(wwReqId, headers),
                credentials: 'same-origin',
                signal,
            });

            if (!response.ok) {
                errorHandler(path, { query: parse(queryString) }, response);
            }
            if (fullResponse) {
                return response as any;
            }
            const data = await response.json();
            return { ...data, wwReqId };
        } catch (error) {
            throw parseException(error, path, { query: parse(queryString) });
        } finally {
            clearTimeout(abortTimeout);
        }
    }

    // eslint-disable-next-line max-params
    public async post(
        path: string,
        body: RequestInit['body'],
        queryParams: object = {},
        headers: object = {},
        errorHandler: ErrorHandlerFunction
    ): Promise<Response> {
        const wwReqId = uuidv4();
        try {
            // we need to keep the await here, because otherwise we cannot catch an error,
            // because it directly return the promise.
            const response = await fetch(this.apiBaseUrl + encodeURI(path) + this.getQueryString(queryParams), {
                method: 'POST',
                headers: this.getHeaders(wwReqId, headers),
                body,
                credentials: 'same-origin',
            });

            if (!response.ok) {
                errorHandler(path, { body, query: queryParams }, response);
            }

            return response;
        } catch (error) {
            throw parseException(error, path, { body, query: queryParams });
        }
    }

    // eslint-disable-next-line max-params
    public async delete(
        path: string,
        body: RequestInit['body'],
        queryParams: object = {},
        headers: object = {},
        errorHandler: ErrorHandlerFunction
    ): Promise<Response> {
        const wwReqId = uuidv4();
        try {
            const response = await fetch(this.apiBaseUrl + encodeURI(path) + this.getQueryString(queryParams), {
                method: 'DELETE',
                headers: this.getHeaders(wwReqId, headers),
                body,
                credentials: 'same-origin',
            });

            if (!response.ok) {
                errorHandler(path, { body, query: queryParams }, response);
            }

            return response;
        } catch (error) {
            throw parseException(error, path, { body, query: queryParams });
        }
    }
}
